import React from 'react';
import { Hidden } from '@material-ui/core';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { Formik, FormikProps } from 'formik';
import { FormField } from '../../../../TCSMApp/global/components/form-element/form-field';
import { useEpiClergySettings, useEpiClergySettingsLoaded } from '../../hooks/useEpiClergySettings';
import { Clergy, ClergyFilters, ClergySearchSort, IsLocalClergy } from './clergy-model';
import { FormButtons } from '../form-element/form-buttons';
import SortButton from '../list/sort-button';
import { PaginatedList } from '../list/paginated-list';
import { GetClergyOrderByOptions } from '../../ducks/clergy-api';

interface Props {
    searchResults: Clergy[];
    onNewSearch: () => void;
    onNavigateBack: () => void;
    searchFilters: ClergyFilters;
    toggleSort: (searchFilters: ClergyFilters, currentSort: ClergySearchSort) => void;
    currentPageIndex: number;
    navigateToPageIndex: (pageIndex: number) => void;
    selectClergyById: (clergyId: string | number) => void;
    searchInProgress: boolean;
    setSort: (searchFilters: ClergyFilters, selectedSort : ClergySearchSort)=> void;
}

function ClergyResults(props: Props) {
    const { searchResults, onNewSearch, onNavigateBack, searchFilters, toggleSort,
        currentPageIndex, navigateToPageIndex, selectClergyById, searchInProgress, setSort } = props;
    const clergySettingsLoaded = useEpiClergySettingsLoaded();
    const clergySettings = useEpiClergySettings();

    const orderByOptions = GetClergyOrderByOptions(clergySettings?.orderByMapping);
    const hasInternationalResults = searchResults?.find(clergy => !IsLocalClergy(clergy));
    const hasLocalResults = searchResults?.find(clergy => IsLocalClergy(clergy));

    const cancelText = !searchInProgress && (!searchResults || searchResults?.length === 0) ? clergySettings?.modifySearchButton : clergySettings?.backButton;

    const initialValues = {
        orderBy: searchFilters?.sort,
    };

    if (!clergySettingsLoaded || !clergySettings) {
        return <LoadingCircle />;
    }  

    function OnSortChanged(e){        
        setSort(searchFilters, e?.target?.value);
    }

    function OnSubmit(values){        
        setSort(searchFilters, values?.orderBy);
    }
    
    function SortSelector(){
        if (searchResults?.length > 0){
            return (<Formik
                initialValues={initialValues}
                onSubmit={OnSubmit}			
                enableReinitialize			
            >
                {(formProps: FormikProps<any>) => (
                    <FormField
                        name='orderBy'                            
                        component='input'  
                        placeholder={clergySettings?.orderByLabel || '- Order By -'}                           
                        touched={formProps.touched}
                        errors={formProps.errors}                           
                        onChange={(orderByChanged)=> OnSortChanged(orderByChanged)}                                                                                                            
                        options={orderByOptions}
                        select
                        />
                )}
            
            </Formik >);
        }
    
        return null;
    }

    function ResultsLabel(): string{
        if (searchInProgress) return null;
        
        if (!searchInProgress && (!searchResults || searchResults?.length === 0)){
            return clergySettings?.noResultsMessage;
        }

        return `${searchInProgress ? '-' : searchResults?.length} ${clergySettings?.recordsFoundLabel || 'record(s) found'}`;
    }

    function NavigationButtons() {
        return (<>
            <Hidden mdUp>
                <div className='quickfind-clergy-results-nav'>
                    <p><strong> { ResultsLabel() }</strong></p>
                    <SortSelector />
                </div>
			</Hidden>
            <Hidden smDown>
                <div className='quickfind-clergy-results-nav'> 
                    <p><strong> { ResultsLabel() }</strong></p>
                </div>
			</Hidden>            
            <div className='quickfind-clergy-results-formbuttons'>
                <FormButtons
                    disabled={false}
                    submitText={clergySettings?.newSearchButton || 'New Search'}
                    cancelText={cancelText}
                    onSubmit={onNewSearch}
                    onCancel={onNavigateBack}
                    submitDisabled={false}
                    hideCancelButton={!cancelText}
                />            
            </div>
        </>);
    }

    function ResultsHeaders() {
        if (!searchInProgress && searchResults?.length === 0) return null;

        return (
        <Hidden smDown>
            <div className='quickfind-results-table-row quickfind-results-table-row-header'>
                <div className='quickfind-results-table-row-cell'>
                    <SortButton
                        isDescending={searchFilters?.sort === ClergySearchSort.LastNameDesc}
                        onToggleSort={() => toggleSort(searchFilters, ClergySearchSort.LastNameDesc)}
                        label={clergySettings?.lastNameLabel} />
                </div>
                <div className='quickfind-results-table-row-cell'>
                    <SortButton
                        isDescending={searchFilters?.sort === ClergySearchSort.FirstNameDesc}
                        onToggleSort={() => toggleSort(searchFilters, ClergySearchSort.FirstNameDesc)}
                        label={clergySettings?.firstMiddleNameLabel} />
                </div>
                <div className='quickfind-results-table-row-cell'>
                    <SortButton
                        isDescending={searchFilters?.sort === ClergySearchSort.CityDesc}
                        onToggleSort={() => toggleSort(searchFilters, ClergySearchSort.CityDesc)}
                        label={clergySettings?.cityLabel} />
                </div>
                <div className='quickfind-results-table-row-cell'>
                    <SortButton
                        isDescending={searchFilters?.sort === ClergySearchSort.StateDesc}
                        onToggleSort={() => toggleSort(searchFilters, ClergySearchSort.StateDesc)}
                        label={clergySettings?.stateLabel} />
                </div>
                <div className='quickfind-results-table-row-cell'>
                    <SortButton
                        isDescending={null}
                        onToggleSort={null}
                        label={clergySettings?.countryLabel}
                        noSort />
                </div>
            </div>
        </Hidden>);
    }

    function RenderListingSeparator(clergyItem: Clergy, itemIndex: number){
        // Only show separators when there are both international 
        // and local results
        if (!(hasLocalResults && hasInternationalResults)) return null;

        let listingSeparator = itemIndex === 0 ? clergySettings?.listingUSTitle : '';
        
        const clergyPreviousItem = searchResults[itemIndex - 1];
        const isPreviousClergyLocal = IsLocalClergy(clergyPreviousItem);
        const isClergyLocal = IsLocalClergy(clergyItem);

        if (isPreviousClergyLocal && !isClergyLocal){
            listingSeparator = clergySettings?.listingInternationalTitle;
        }

        if (!listingSeparator) return null;

        return (
        <>
            <Hidden smDown>
                <div className='quickfind-results-table-row' key={listingSeparator}>
                    <div className='quickfind-clergy-results-title'>
                        {listingSeparator}
                    </div>
                </div>
            </Hidden>
            <Hidden mdUp>
                <div className='quickfind-clergy-results-title' key={listingSeparator}>                    
                    {listingSeparator}                    
                </div>
            </Hidden>
        </>
        );
    }

    function ClergyRow(clergyItem: Clergy, itemIndex: number) {
        return (<>        
        { RenderListingSeparator(clergyItem, itemIndex) }         
        <Hidden smDown>
            <div className='quickfind-results-table-row' key={clergyItem?.guid}>
                <div className='quickfind-results-table-row-cell'><button className='quickfind-results-table-link' type='button' onClick={() => selectClergyById(clergyItem?.guid)} key={clergyItem?.guid}>{clergyItem?.last_name}</button></div>
                <div className='quickfind-results-table-row-cell'><button className='quickfind-results-table-link' type='button' onClick={() => selectClergyById(clergyItem?.guid)} key={clergyItem?.guid}>{clergyItem?.first_name} {clergyItem?.middle_name}</button></div>
                <div className='quickfind-results-table-row-cell'>{clergyItem?.street_city}</div>
                <div className='quickfind-results-table-row-cell'>{clergyItem?.street_state}</div>
                <div className='quickfind-results-table-row-cell'>{clergyItem?.street_country}</div>
            </div>
        </Hidden>
        <Hidden mdUp>
            <div className='quickfind-clergy-results-item' key={clergyItem?.guid}>
                <div className='quickfind-clergy-results-item-content'>
                    <div className='quickfind-clergy-results-item-label'>
                        {clergySettings?.lastNameLabel}
                    </div>
                    <div className='quickfind-clergy-results-item-value'>
                        <button className='quickfind-clergy-results-item-link' type='button' onClick={() => selectClergyById(clergyItem?.guid)} key={clergyItem?.guid}>{clergyItem?.last_name}</button>
                    </div>
                </div>
                <div className='quickfind-clergy-results-item-content'>
                    <div className='quickfind-clergy-results-item-label'>
                        {clergySettings?.firstMiddleNameLabel}
                    </div>
                    <div className='quickfind-clergy-results-item-value'>
                        <button className='quickfind-clergy-results-item-link' type='button' onClick={() => selectClergyById(clergyItem?.guid)} key={clergyItem?.guid}>{clergyItem?.first_name} {clergyItem?.middle_name}</button>
                    </div>
                </div>
                <div className='quickfind-clergy-results-item-content'>
                    <div className='quickfind-clergy-results-item-label'>
                        {clergySettings?.cityLabel}
                    </div>
                    <div className='quickfind-clergy-results-item-value'>
                        {clergyItem?.street_city}
                    </div>
                </div>  
                <div className='quickfind-clergy-results-item-content'>
                    <div className='quickfind-clergy-results-item-label'>
                        {clergySettings?.stateLabel}
                    </div>
                    <div className='quickfind-clergy-results-item-value'>
                        {clergyItem?.street_state}
                    </div>
                </div>  
                <div className='quickfind-clergy-results-item-content'>
                    <div className='quickfind-clergy-results-item-label'>
                        {clergySettings?.countryLabel}
                    </div>
                    <div className='quickfind-clergy-results-item-value'>
                        {clergyItem?.street_country}
                    </div>
                </div>                                                
            </div>
        </Hidden>
        </>);
    }

    return (
        <>
            <div>
                <h3 className='ecd-page-content-title'>
                    {clergySettings.searchResultTitle}
                </h3>
                <div className='ecd-page-content-section'>
                    <NavigationButtons />
                    <div>
                        <PaginatedList<Clergy>
                            renderHeaders={ResultsHeaders}
                            renderListItem={ClergyRow}
                            completeList={searchResults}
                            currentPageIndex={currentPageIndex}
                            emptyMessage=''
                            navigateToPageIndex={navigateToPageIndex}
                            searchInProgress={searchInProgress} />
                    </div>
                </div>
            </div>
        </>
    );
}

export default ClergyResults;
